import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import { company } from "@/data/company";

function Icon({ name, className }) {
  const C = Icons[name] || Icons.Sparkles;
  return <C className={className} />;
}

export default function About() {
  return (
    <>
      <SEO
        title="About"
        description="Envago Labs develops modern AI-powered applications focused on education, productivity and meaningful technology."
        path="/about"
      />

      <section className="relative overflow-hidden gradient-emerald">
        <div className="grain absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-6xl px-5 pb-16 pt-36 sm:px-8 md:pt-44">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              About us
            </span>
            <h1 className="mt-5 font-display text-5xl font-light leading-none tracking-tighter md:text-6xl">
              A studio for meaningful AI.
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
        <div className="grid gap-12 md:grid-cols-12">
          <Reveal className="md:col-span-4">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Who we are
            </span>
          </Reveal>
          <Reveal delay={0.1} className="space-y-6 md:col-span-8">
            <p className="font-display text-2xl font-light leading-snug tracking-tight md:text-3xl">
              Envago Labs develops modern AI-powered applications focused on education,
              productivity and meaningful technology.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              We're an independent software studio building a growing family of apps under one
              trusted brand. Our first product, {" "}
              <a
                href="https://tanzeenplus.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary hover:underline"
                data-testid="about-tanzeen-link"
              >
                Tanzeen+
              </a>
              , is an AI-powered Islamic learning platform that brings the Quran, prayer and guided
              learning into a single, thoughtfully designed experience.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              We believe technology should be clear, respectful and genuinely useful. Every product
              we ship is held to a high standard of design, performance and privacy — without
              exaggerated claims or dark patterns.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              What guides us
            </span>
            <h2 className="mt-4 font-display text-3xl font-light tracking-tight md:text-4xl">
              Our core values.
            </h2>
          </Reveal>
          <Stagger className="mt-12 grid gap-6 sm:grid-cols-2">
            {company.values.map((v) => (
              <motion.div
                key={v.title}
                variants={staggerItem}
                className="flex gap-5 rounded-2xl border border-border bg-card p-7"
              >
                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10">
                  <Icon name={v.icon} className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-medium tracking-tight">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {v.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>
    </>
  );
}
